<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Compositions</h1>
        <p class="page-subtitle">Lier les ingrédients aux produits composés</p>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <v-row>
        <v-col cols="12" md="4">
          <v-card flat class="table-card">
            <div class="pa-3">
              <input v-model="searchProduct" type="text" placeholder="Rechercher produit..." class="search-input" />
            </div>
            <v-divider></v-divider>
            <div class="product-list">
              <div
                v-for="p in filteredProducts"
                :key="p.id"
                class="product-row"
                :class="selectedProduct && selectedProduct.id === p.id ? 'product-row--active' : ''"
                @click="selectProduct(p)"
              >
                <div class="product-row__name">{{ p.name }}</div>
                <div class="product-row__meta">
                  <span class="text--secondary text-caption">{{ p.compositions_count || 0 }} ingrédient{{ (p.compositions_count || 0) > 1 ? 's' : '' }}</span>
                </div>
              </div>
              <div v-if="filteredProducts.length === 0" class="text-center text--secondary pa-6 text-caption">Aucun produit</div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <v-card flat class="table-card" v-if="selectedProduct">
            <div class="pa-3 d-flex align-center justify-space-between">
              <div>
                <div class="font-weight-bold">{{ selectedProduct.name }}</div>
                <div class="text-caption text--secondary">Composition</div>
              </div>
              <v-btn color="primary" depressed small @click="addRow"><v-icon left small>mdi-plus</v-icon>Ingrédient</v-btn>
            </div>
            <v-divider></v-divider>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th class="text-left">INGRÉDIENT</th>
                  <th class="text-right" style="width: 130px">QUANTITÉ</th>
                  <th class="text-left" style="width: 110px">UNITÉ</th>
                  <th style="width: 50px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in compositionRows" :key="idx">
                  <td>
                    <v-select v-model="row.ingredient_id" :items="ingredients" item-text="name" item-value="id" outlined dense hide-details></v-select>
                  </td>
                  <td>
                    <v-text-field v-model.number="row.quantity" type="number" min="0" step="0.01" outlined dense hide-details reverse></v-text-field>
                  </td>
                  <td>
                    <v-select v-model="row.unit" :items="unitOptions" outlined dense hide-details></v-select>
                  </td>
                  <td class="text-center">
                    <v-btn icon x-small @click="removeRow(idx)"><v-icon small color="error">mdi-delete</v-icon></v-btn>
                  </td>
                </tr>
                <tr v-if="compositionRows.length === 0">
                  <td colspan="4" class="text-center text--secondary pa-6">Aucun ingrédient lié</td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-divider></v-divider>
            <div class="d-flex justify-end pa-3">
              <v-btn color="primary" depressed :loading="saving" :disabled="saving || !canSave" @click="saveComposition">Enregistrer</v-btn>
            </div>
          </v-card>
          <v-card flat class="table-card" v-else>
            <div class="text-center text--secondary pa-10">Sélectionnez un produit dans la liste</div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

export default {
  name: 'CompositionsView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    items: [{ text: 'Compositions', disabled: true, href: '/compositions' }],
    enable: false,
    saving: false,
    products: [],
    ingredients: [],
    selectedProduct: null,
    compositionRows: [],
    searchProduct: '',
    unitOptions: ['mg', 'g', 'kg', 't', 'lb', 'oz', 'ml', 'cl', 'dl', 'l', 'gal', 'qt', 'pt', 'floz', 'cup', 'tbsp', 'tsp', 'unité', 'paire', 'demi-douzaine', 'douzaine', 'centaine', 'millier'],
  }),
  computed: {
    filteredProducts() {
      if (!this.searchProduct.trim()) return this.products
      var q = this.searchProduct.toLowerCase()
      return this.products.filter(function (p) { return p.name && p.name.toLowerCase().indexOf(q) !== -1 })
    },
    canSave() {
      return this.compositionRows.every(function (r) { return r.ingredient_id && r.quantity > 0 && r.unit })
    },
  },
  methods: {
    async load() {
      var self = this
      try {
        var [products, ingredients] = await Promise.all([
          this.$axios.get('/products?orderasc=name&include=compositions'),
          this.$axios.get('/ingredients?orderasc=name'),
        ])
        self.products = (products.data || []).map(function (p) {
          return Object.assign({}, p, { compositions_count: (p.compositions || []).length })
        })
        self.ingredients = ingredients.data || []
      } catch (e) { /* ignore */ }
      self.enable = true
    },
    async selectProduct(p) {
      this.selectedProduct = p
      var self = this
      var r = await this.$axios.get('/product-compositions?product_id=' + p.id + '&include=ingredient&orderasc=id')
      var data = r.data || []
      self.compositionRows = data.map(function (row) {
        return { ingredient_id: row.ingredient_id, quantity: parseFloat(row.quantity), unit: row.unit }
      })
    },
    addRow() { this.compositionRows.push({ ingredient_id: null, quantity: 1, unit: 'g' }) },
    removeRow(idx) { this.compositionRows.splice(idx, 1) },
    async saveComposition() {
      if (!this.selectedProduct) return
      this.saving = true
      try {
        await this.$axios.post('/product-compositions/bulk', {
          product_id: this.selectedProduct.id,
          items: this.compositionRows,
        })
        var idx = this.products.findIndex(p => p.id === this.selectedProduct.id)
        if (idx > -1) {
          this.$set(this.products, idx, Object.assign({}, this.products[idx], { compositions_count: this.compositionRows.length }))
          this.selectedProduct = this.products[idx]
        }
      } finally { this.saving = false }
    },
  },
  mounted() { this.load() },
}
</script>

<style scoped>
.search-input { width: 100%; padding: 8px 12px; font-size: 0.8125rem; border: 1px solid #cbd5e1; border-radius: 6px; outline: none; }
.search-input:focus { border-color: #194173; box-shadow: 0 0 0 2px rgba(25, 65, 115, 0.1); }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; overflow: hidden; }
.product-list { max-height: 600px; overflow-y: auto; }
.product-row { padding: 10px 16px; border-bottom: 1px solid #f1f5f9; cursor: pointer; }
.product-row:hover { background: rgba(25, 65, 115, 0.04); }
.product-row--active { background: rgba(25, 65, 115, 0.08); }
.product-row__name { font-size: 0.875rem; font-weight: 600; color: #1a2332; }
.product-row__meta { font-size: 0.75rem; }
</style>
